import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config/api';
import '../styles/WarrantyConfirmationModal.css';

const WarrantyConfirmationModal = ({
  orderNo,
  customerName,
  orderType,
  onConfirm,
  onCancel,
  onWarrantyStarted
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [warrantyExists, setWarrantyExists] = useState(false);
  const [existingWarranty, setExistingWarranty] = useState(null);

  // Check if warranty already exists
  useEffect(() => {
    const checkWarranty = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/orders/${orderNo}/warranty`);
        const warrantyData = response.data;

        console.log('🔍 Checking existing warranty:', warrantyData);

        const hasWarranty = warrantyData.Wig_Warranty_Start_Date && warrantyData.Wig_Warranty_Expires_Date;
        const resetCount = warrantyData.WarrantyResetCount || 0;

        // If warranty has already been reset once, close the modal immediately
        if (hasWarranty && resetCount >= 1) {
          console.log('⏭️ Warranty already reset - maximum resets reached');
          setError('Warranty has already been reset once. Maximum resets reached.');
          // Auto-close after showing error
          setTimeout(() => {
            if (onCancel) {
              onCancel();
            }
          }, 3000);
          setIsLoading(false);
          return;
        }

        setWarrantyExists(hasWarranty);
        if (hasWarranty) {
          setExistingWarranty({
            startDate: warrantyData.Wig_Warranty_Start_Date,
            endDate: warrantyData.Wig_Warranty_Expires_Date,
            resetCount: resetCount
          });
        }
      } catch (error) {
        console.error('⚠️ Error checking warranty:', error);
        // Assume no warranty if check fails
        setWarrantyExists(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkWarranty();
  }, [orderNo, onCancel]);

  const handleStartWarranty = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      // Always reset warranty to 9 months when balance is paid off
      console.log('🔄 Resetting warranty to 9 months for order:', orderNo);
      const response = await axios.post(`${API_URL}/api/orders/${orderNo}/reset-warranty`);
      console.log('✅ Warranty reset successfully:', response.data);

      // Notify parent components
      if (onWarrantyStarted) {
        onWarrantyStarted({
          success: true,
          warranty: response.data.warranty,
          message: 'Warranty reset successfully for 9 months',
          isReset: true
        });
      }

      if (onConfirm) {
        onConfirm();
      }
    } catch (error) {
      console.error('❌ Failed to reset warranty:', error);

      let errorMessage = 'Failed to reset warranty';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSkip = () => {
    console.log('⏭️ User skipped warranty start');
    if (onCancel) {
      onCancel();
    }
  };

  // Show loading state while checking warranty
  if (isLoading) {
    return (
      <div className="warranty-modal-overlay">
        <div className="warranty-modal-content">
          <div className="warranty-modal-body" style={{ textAlign: 'center', padding: '40px 20px' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '32px', color: '#28a745', marginBottom: '16px' }}></i>
            <p>Checking warranty status...</p>
            {error && (
              <div className="warranty-error-alert" style={{ marginTop: '20px' }}>
                <i className="fas fa-exclamation-triangle"></i>
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="warranty-modal-overlay">
      <div className="warranty-modal-content">
        <div className="warranty-modal-header">
          <div className="warranty-modal-icon">
            <i className="fas fa-shield-alt"></i>
          </div>
          <h3 className="warranty-modal-title">
            Reset Warranty?
          </h3>
        </div>

        <div className="warranty-modal-body">
          <p className="warranty-message">
            The balance for <strong>Order #{orderNo}</strong> has been paid in full.
          </p>

          {existingWarranty && (
            <div className="warranty-info-box" style={{ backgroundColor: '#e8f5e9', borderColor: '#4caf50', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <i className="fas fa-shield-alt" style={{ color: '#2e7d32' }}></i>
                <strong style={{ color: '#2e7d32' }}>Current Warranty Status</strong>
              </div>
              <div className="warranty-info-row" style={{ borderBottom: 'none', paddingTop: '4px' }}>
                <span className="warranty-label">Current Expiration:</span>
                <span className="warranty-value">{new Date(existingWarranty.endDate).toLocaleDateString()}</span>
              </div>
              {existingWarranty.resetCount > 0 && (
                <div className="warranty-info-row" style={{ borderBottom: 'none', paddingTop: '4px' }}>
                  <span className="warranty-label">Previous Resets:</span>
                  <span className="warranty-value">{existingWarranty.resetCount}</span>
                </div>
              )}
            </div>
          )}

          {!warrantyExists && (
            <div className="warranty-info-box" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="fas fa-exclamation-triangle" style={{ color: '#856404' }}></i>
                <strong style={{ color: '#856404' }}>No warranty found for this order</strong>
              </div>
            </div>
          )}

          <div className="warranty-info-box">
            <div className="warranty-info-row">
              <span className="warranty-label">Customer:</span>
              <span className="warranty-value">{customerName}</span>
            </div>
            <div className="warranty-info-row">
              <span className="warranty-label">Order Type:</span>
              <span className="warranty-value">{orderType}</span>
            </div>
            <div className="warranty-info-row">
              <span className="warranty-label">New Warranty Duration:</span>
              <span className="warranty-value warranty-highlight">
                9 Months (270 Days)
              </span>
            </div>
          </div>

          <p className="warranty-question">
            Would you like to reset the warranty to a new 9-month period?
          </p>

          {error && (
            <div className="warranty-error-alert">
              <i className="fas fa-exclamation-triangle"></i>
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="warranty-modal-footer">
          <button
            type="button"
            onClick={handleSkip}
            className="warranty-btn warranty-btn-secondary"
            disabled={isProcessing}
          >
            <i className="fas fa-times"></i>
            Not Yet
          </button>
          <button
            type="button"
            onClick={handleStartWarranty}
            className="warranty-btn warranty-btn-primary"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Resetting Warranty...
              </>
            ) : (
              <>
                <i className="fas fa-shield-alt"></i>
                Yes, Reset Warranty
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WarrantyConfirmationModal;
